import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Badge, Checkbox, Paper, Text } from "@mantine/core"
import { IconCalendarEvent } from "@tabler/icons-react"
import { displayMeetingTitle } from "../../../../lib/meeting-title"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/**
 * JargonMeetingGroup wraps a meeting's suggestions in one bordered card — a header
 * (its subject, a count badge, an optional select-all checkbox, and optional
 * right-aligned bulk actions) sitting above a divided list of rows. The card is
 * what makes a group read as a group; the rows carry their own top border, so
 * they render flush inside it with no nested Papers. Dumb component: it owns no
 * selection state, only renders what it's told and reports toggles.
 */
export function JargonMeetingGroup({
  subject,
  count,
  selectable = false,
  allSelected = false,
  someSelected = false,
  onToggleAll,
  headerActions,
  children,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const label = subject === null ? i18n._(msg`Unknown meeting`) : displayMeetingTitle(subject, null, i18n)

  return (
    <Paper withBorder radius="md" component="section" className={classes.group}>
      <div className={classes.header}>
        {selectable && (
          <Checkbox
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            onChange={() => onToggleAll?.()}
            aria-label={i18n._(msg`Select all suggestions from ${label}`)}
            className={classes.check}
          />
        )}
        <IconCalendarEvent size={15} className={classes.icon} aria-hidden />
        <Text span fw={600} size="sm" className={classes.subject}>
          {label}
        </Text>
        <Badge size="sm" variant="light" color="brand" className={classes.count}>
          <span key={count} className="lex-count-tick">
            {count}
          </span>
        </Badge>
        {headerActions && <div className={classes.headerActions}>{headerActions}</div>}
      </div>
      <div className={classes.rows}>{children}</div>
    </Paper>
  )
}
